import { useEffect, useState } from "react";
import styles from "../css/ModalResult.module.css";
import { useTranslation, Trans } from "react-i18next";
import InfoBox from "./InfoBox";
import ButtonBtn from "./buttons/ButtonBtn";
import FooterLogin from "./FooterLogin";
import CloseBtn from "./CloseBtn";

const ModalResult = ({
  onClose,
  iconSrc = null,
  title,
  subtitle = null,
  description = null,
  infoList = [],
  buttonText,
  onButtonClick,
  showCheckbox = false,
  checkboxText = "",
  requireCheckbox = false,
  buttonOutside = false,
  turnOffOpt = true, // Volitelná možnost vypnutí modalu
  fullWidth = false, // Nový prop pro vypnutí max-width
  contentOutsideContainer = false, // Nový prop pro zobrazení obsahu mimo kontejner
  showCloseButton = false, // Nový prop pro zobrazení křížku
  iconScale = 1, // Volitelně zvětšit/zmenšit ikonu
}) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    // Disable scrolling on both body and html
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    // Only apply lock if it's not already hidden to avoid stomping nested modals
    const changedBody = originalBodyOverflow !== "hidden";
    const changedHtml = originalHtmlOverflow !== "hidden";

    if (changedBody) document.body.style.overflow = "hidden";
    if (changedHtml) document.documentElement.style.overflow = "hidden";

    // Keyboard accessibility
    const handleEscape = (e) => {
      if (e.key === "Escape" && onClose && turnOffOpt) {
        onClose();
      }
    };

    if (turnOffOpt) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      // Restore only if we changed it here
      if (changedBody) document.body.style.overflow = originalBodyOverflow;
      if (changedHtml)
        document.documentElement.style.overflow = originalHtmlOverflow;
      if (turnOffOpt) {
        document.removeEventListener("keydown", handleEscape);
      }
    };
  }, [onClose, turnOffOpt]);

  const handleButtonClick = () => {
    if (requireCheckbox && !isChecked) {
      return; // Pokud je checkbox povinný a není zaškrtnutý, nedělej nic
    }

    if (onButtonClick) {
      onButtonClick(isChecked);
    } else if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose && turnOffOpt) {
      onClose();
    }
  };

  return (
    <div
      className={`${styles.overlay} ${
        contentOutsideContainer ? styles.overlayWithOutsideContent : ""
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={handleBackdropClick}
    >
      {showCloseButton && onClose && <CloseBtn onClick={onClose} />}
      {contentOutsideContainer ? (
        // Pokud je contentOutsideContainer true, zobrazujeme jen description přímo v overlay + footer
        <>
          {description &&
            (typeof description === "string" ? (
              <p className={styles.outsideContent}>{description}</p>
            ) : (
              <div className={styles.outsideContent}>{description}</div>
            ))}

          <div className={styles.footerContainer}>
            <FooterLogin />
          </div>
        </>
      ) : (
        // Původní struktura s kontejnerem
        <>
          <div className={styles.contentWrapper}>
            <div
              className={`${styles.container} ${
                fullWidth ? styles.fullWidth : ""
              }`}
            >
              {iconSrc && (
                <img
                  className={styles.icon}
                  src={iconSrc}
                  alt=""
                  style={
                    iconScale !== 1
                      ? { transform: `scale(${iconScale})` }
                      : undefined
                  }
                />
              )}

              {title && (
                <div className={styles.titleSection}>
                  <h2 id="modal-title">{title}</h2>
                  {subtitle && (
                    <p className={styles.subtitle}>
                      {typeof subtitle === "string" ? subtitle : subtitle}
                    </p>
                  )}
                </div>
              )}

              {description &&
                (typeof description === "string" ? (
                  <p className={styles.description}>{description}</p>
                ) : (
                  <div className={styles.description}>{description}</div>
                ))}
              {infoList.length > 0 && (
                <div className={`${styles.info} ${styles.infoBoxWrapper}`}>
                  {infoList.map((item, index) => (
                    <InfoBox
                      key={index}
                      number={index + 1}
                      title={item.title}
                      text={item.text}
                      isChecked={isChecked}
                    />
                  ))}
                </div>
              )}

              {showCheckbox && (
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    name="checkbox"
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                  />
                  <span className={styles.checkboxChecker}></span>
                  <span className={styles.checkboxLabel}>{checkboxText}</span>
                </label>
              )}

              {!buttonOutside && (
                <div className={styles.buttonContainer}>
                  <ButtonBtn
                    justifyContent="center"
                    disabled={requireCheckbox && !isChecked}
                    onClick={handleButtonClick}
                  >
                    {buttonText}
                  </ButtonBtn>
                </div>
              )}
            </div>

            {buttonOutside && (
              <ButtonBtn
                justifyContent="center"
                disabled={requireCheckbox && !isChecked}
                onClick={handleButtonClick}
              >
                {buttonText}
              </ButtonBtn>
            )}
          </div>

          <div className={styles.footerContainer}>
            <FooterLogin />
          </div>
        </>
      )}
    </div>
  );
};

/**
 * ModalResult - Obecná komponenta modálního okna pro různé scénáře výsledků
 *
 * @component
 * @param {Object} props - Vlastnosti komponenty
 * @param {Function} props.onClose - Funkce volaná při zavření modálního okna
 * @param {string} [props.iconSrc=null] - Volitelný zdroj ikony pro modální okno
 * @param {string} props.title - Hlavní titulek modálního okna
 * @param {string} [props.subtitle=null] - Volitelný podtitulek pod titulkem
 * @param {string} [props.description=null] - Volitelný popisný text
 * @param {Array} [props.infoList=[]] - Pole informačních položek zobrazených jako číslované boxy
 * @param {string} props.infoList[].title - Titulek informační položky
 * @param {string|React.Element} props.infoList[].text - Text nebo React element pro informační položku
 * @param {string} props.buttonText - Text pro akční tlačítko
 * @param {Function} [props.onButtonClick] - Funkce volaná při kliknutí na tlačítko. Pokud není zadána, použije se onClose
 * @param {boolean} [props.showCheckbox=false] - Zda zobrazit zaškrtávací políčko
 * @param {string} [props.checkboxText=""] - Text pro popisek zaškrtávacího políčka
 * @param {boolean} [props.requireCheckbox=false] - Zda musí být políčko zaškrtnuto pro aktivaci tlačítka
 * @param {boolean} [props.buttonOutside=false] - Zda zobrazit tlačítko mimo kontejner modalu
 * @param {boolean} [props.turnOffOpt=true] - Zda umožnit uživateli zavřít modal (ESC klávesa, klik na pozadí)
 * @param {boolean} [props.fullWidth=false] - Zda vypnout max-width omezení kontejneru pro plnou šířku
 * @param {boolean} [props.contentOutsideContainer=false] - Zda zobrazit obsah přímo v overlay bez kontejneru
 * @param {boolean} [props.showCloseButton=false] - Zda zobrazit křížek pro zavření modalu
 * @param {number} [props.iconScale=1] - Měřítko pro ikonu (např. 2 pro dvojnásobnou velikost)
 *
 * @example
 * // Základní úspěšný modal
 * <ModalResult
 *   onClose={() => setShowModal(false)}
 *   title="Úspěch!"
 *   subtitle="Operace byla dokončena"
 *   description="Vaše nastavení bylo úspěšně uloženo."
 *   buttonText="Pokračovat"
 * />
 *
 * @example
 * // Chybový modal s ikonou
 * <ModalResult
 *   onClose={() => setShowModal(false)}
 *   iconSrc="/icons/error.svg"
 *   title="Chyba"
 *   description="Nastala neočekávaná chyba. Zkuste to prosím znovu."
 *   buttonText="Zkusit znovu"
 *   onButtonClick={() => handleRetry()}
 * />
 *
 * @example
 * // Informační modal s číslovaným seznamem
 * <ModalResult
 *   onClose={() => setShowModal(false)}
 *   title="Důležité informace"
 *   infoList={[
 *     { title: "První krok", text: "Ujistěte se, že máte všechna potřebná oprávnění." },
 *     { title: "Druhý krok", text: "Zkontrolujte připojení k internetu." },
 *     { title: "Třetí krok", text: "Klikněte na tlačítko Pokračovat." }
 *   ]}
 *   buttonText="Rozumím"
 * />
 *
 * @example
 * // Potvrzovací modal s povinným zaškrtávacím políčkem
 * <ModalResult
 *   onClose={() => setShowModal(false)}
 *   title="Smazání dat"
 *   description="Tato akce je nevratná. Všechna data budou trvale smazána."
 *   showCheckbox={true}
 *   checkboxText="Rozumím, že tato akce je nevratná"
 *   requireCheckbox={true}
 *   buttonText="Smazat"
 *   onButtonClick={(isChecked) => handleDelete()}
 * />
 *
 * @example
 * // Modal s internacionalizací
 * <ModalResult
 *   onClose={() => setShowModal(false)}
 *   title={t('modal.success.title')}
 *   subtitle={
 *     <Trans
 *       i18nKey="modal.success.subtitle"
 *       components={[<strong />]}
 *     />
 *   }
 *   description={t('modal.success.description')}
 *   buttonText={t('common.continue')}
 * />
 *
 * @example
 * // Modal bez možnosti zavření (kritická zpráva)
 * <ModalResult
 *   title="Důležité upozornění"
 *   description="Tato akce vyžaduje vaši pozornost. Modal nelze zavřít bez akce."
 *   buttonText="Potvrdit"
 *   turnOffOpt={false}
 *   onButtonClick={() => handleCriticalAction()}
 * />
 *
 * @example
 * // Modal s plnou šířkou pro široký obsah
 * <ModalResult
 *   title="Ceník"
 *   description={<PriceTable />}
 *   buttonText="Zavřít"
 *   fullWidth={true}
 *   onClose={() => setShowModal(false)}
 * />
 *
 * @example
 * // Modal s obsahem mimo kontejner (jen overlay + obsah)
 * <ModalResult
 *   description={<PriceList />}
 *   contentOutsideContainer={true}
 *   onClose={() => setShowModal(false)}
 * />
 *
 * @example
 * // Modal s křížkem pro zavření
 * <ModalResult
 *   title="Upozornění"
 *   description="Toto je důležitá zpráva."
 *   showCloseButton={true}
 *   onClose={() => setShowModal(false)}
 * />
 */

export default ModalResult;
